import React from "react";
import { CARD_SIZE } from "@/lib/cardImage";

const HEAD = "'Cabinet Grotesk', 'Plus Jakarta Sans', sans-serif";
const MONO = "'IBM Plex Mono', monospace";
const BODY = "'Plus Jakarta Sans', sans-serif";

function Stat({ value, label }) {
  return (
    <div>
      <p style={{ fontFamily: HEAD, fontSize: 84, fontWeight: 900, lineHeight: 1, color: "#fff", margin: 0, letterSpacing: -2 }}>{value}</p>
      <p style={{ fontFamily: MONO, fontSize: 20, textTransform: "uppercase", letterSpacing: 3, color: "#A0A6B2", margin: "10px 0 0" }}>{label}</p>
    </div>
  );
}

// 1080x1080 social export of the Impact Card. Inline styles only so html-to-image captures exactly what's here.
export default function ImpactCardExport({ data, forwardRef }) {
  const { display_name, milestone, moves_created, blockers_cleared, actions_completed, action_rate, time_saved_label, money_impact, minutescore, tier, score_status } = data;
  const stats = [
    { v: moves_created ?? blockers_cleared ?? 0, l: "moves created" },
    { v: actions_completed ?? 0, l: "actions completed" },
    { v: `${action_rate ?? 0}%`, l: "follow-through" },
  ];
  if (time_saved_label !== undefined) stats.push({ v: time_saved_label, l: "decision time saved" });
  const showScore = minutescore !== undefined && minutescore !== null;

  return (
    <div ref={forwardRef} data-testid="impact-card-export"
      style={{
        width: CARD_SIZE, height: CARD_SIZE, boxSizing: "border-box", padding: 88, position: "relative", overflow: "hidden",
        display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: BODY,
        background: "linear-gradient(155deg, #15181D 0%, #0B0C0E 60%, #0A0705 100%)", color: "#fff",
      }}>
      <div style={{ position: "absolute", right: -160, top: -160, width: 560, height: 560, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,107,53,0.26), transparent 70%)" }} />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 10, background: "#FF6B35" }} />

      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <span style={{ width: 64, height: 64, borderRadius: 14, background: "#FF6B35", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
            </svg>
          </span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <span style={{ fontFamily: HEAD, fontSize: 40, fontWeight: 800, letterSpacing: -1 }}>MinuteSwap</span>
            <span style={{ fontFamily: MONO, fontSize: 16, textTransform: "uppercase", letterSpacing: 6, color: "#FF6B35", marginTop: 6 }}>Impact</span>
          </div>
        </div>

        {milestone && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12, marginTop: 56, padding: "14px 24px", borderRadius: 999, border: "2px solid rgba(255,107,53,0.45)", background: "rgba(255,107,53,0.12)", fontFamily: MONO, fontSize: 20, fontWeight: 600, textTransform: "uppercase", letterSpacing: 3, color: "#FF6B35" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22V4" /><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1" /></svg>
            {milestone}
          </span>
        )}

        <p style={{ fontFamily: HEAD, fontSize: 72, fontWeight: 900, letterSpacing: -2.5, lineHeight: 1.05, margin: milestone ? "36px 0 0" : "64px 0 0", color: "#fff" }}>{display_name}</p>
      </div>

      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 56, columnGap: 40 }}>
        {stats.map((s, i) => <Stat key={i} value={s.v} label={s.l} />)}
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32, paddingTop: 44, borderTop: "2px solid rgba(255,255,255,0.10)" }}>
        <div>
          {money_impact !== undefined ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontFamily: HEAD, fontSize: 56, fontWeight: 900, color: "#10B981", letterSpacing: -1.5 }}>£{money_impact}</span>
              <span style={{ fontFamily: MONO, fontSize: 18, textTransform: "uppercase", letterSpacing: 3, color: "#A0A6B2" }}>reported impact</span>
            </div>
          ) : null}
          <p style={{ fontFamily: MONO, fontSize: 20, textTransform: "uppercase", letterSpacing: 5, color: "#8B919E", margin: money_impact !== undefined ? "22px 0 0" : 0 }}>One problem. One move.</p>
        </div>
        {showScore && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, padding: "22px 30px", borderRadius: 28, background: "rgba(255,255,255,0.05)", border: "2px solid rgba(255,255,255,0.08)" }}>
            <span style={{ fontFamily: MONO, fontSize: 16, textTransform: "uppercase", letterSpacing: 4, color: "#A0A6B2" }}>{score_status === "provisional" ? "Provisional MinuteScore" : "MinuteScore"}</span>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontFamily: HEAD, fontSize: 84, fontWeight: 900, lineHeight: 1, letterSpacing: -2 }}>{minutescore}</span>
              {tier && <span style={{ fontSize: 22, fontWeight: 600, color: "#FF6B35" }}>{tier}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
